import { Box, Button, Stack } from '@mui/material';
import tokens from '@verifime/design-tokens';
import ActionsButton from '../ActionsButton';
import { ColoredIcon } from '../ColoredIconAndChip';
import type { TActions } from '../ActionsButton';
import { TDocumentVerificationStatusActions } from './types';

/**
 * Component for DVS status action buttons
 */
export const DvsStatusActions = ({
  onResetStatus,
  onResubmit,
  resubmitDisabled,
  hideResubmit = false,
}: Readonly<{
  onResetStatus: (status: keyof TDocumentVerificationStatusActions) => void;
  onResubmit: () => void;
  resubmitDisabled: boolean;
  hideResubmit?: boolean;
}>) => {
  // Create a properly typed actions mapping.
  // The key is the menu label only; the status actually written is the argument to onResetStatus.
  const actionsMapping: TActions<string> = {
    Passed: {
      iconName: 'checkCircleRounded',
      color: { semantic: 'success' },
      onActionClick: () => onResetStatus('Passed'),
    },
    Failed: {
      iconName: 'errorRounded',
      color: { semantic: 'error' },
      onActionClick: () => onResetStatus('Failed'),
    },
    DataError: {
      iconName: 'errorRounded',
      color: { semantic: 'error' },
      onActionClick: () => onResetStatus('DataError'),
    },
    // States that DVS has no capability to check this document, a foreign document being the
    // typical case. Neutral rather than error styled: nothing has gone wrong, there is simply no
    // source to check against.
    'Not Applicable': {
      iconName: 'removeCircleRounded',
      color: { semantic: 'info' },
      onActionClick: () => onResetStatus('NotApplicable'),
    },
  };

  return (
    <Stack gap={tokens.spacingXs} justifyContent="center" alignItems="center">
      {!hideResubmit && (
        <Box>
          <Button
            startIcon={<ColoredIcon iconName="pending" />}
            variant="outlined"
            onClick={onResubmit}
            disabled={resubmitDisabled}
            data-action="dvs-resubmit"
          >
            Re Submit
          </Button>
        </Box>
      )}
      <ActionsButton
        startIcon={<ColoredIcon iconName="pending" />}
        variant="outlined"
        buttonName="Set Status"
        actions={actionsMapping}
        data-action="dvs-set-status"
      />
    </Stack>
  );
};
